import React, { useMemo, useState } from 'react';
import { ResponsiveContainer, ComposedChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend, Line, Bar } from 'recharts';
import { calculateCGP, calculateCGE } from '@utils/metrics/cgp.js';
import { CLIMBING_STYLES } from '@utils/constants.js';

const CGPCGEChart = ({
  data,
  climbingStyle
}) => {
  const [grouping, setGrouping] = useState('quarterly'); // 'monthly' or 'quarterly'
  const [showEfficiency, setShowEfficiency] = useState(false);

  const chartData = useMemo(() => {
    if (!data?.length) return [];

    // Filter data with valid grades
    const validData = data.filter(ascent => {
      const grade = climbingStyle === CLIMBING_STYLES.BOULDER ? ascent.vgrade : ascent.ewbanks;
      return grade !== null && !isNaN(grade);
    }).sort((a, b) => a['Ascent Date'].localeCompare(b['Ascent Date']));

    if (!validData.length) return [];

    const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    // Derive period key from an ascent's UTC-correct year/month fields
    const periodKeyFor = (ascent) => {
      if (grouping === 'quarterly') {
        const q = Math.floor((ascent.month - 1) / 3) + 1;
        return { key: `${ascent.year}-Q${q}`, label: `${ascent.year} Q${q}` };
      }
      const mm = String(ascent.month).padStart(2, '0');
      return {
        key: `${ascent.year}-${mm}`,
        label: `${String(ascent.year).slice(2)} ${MONTH_SHORT[ascent.month - 1]}`
      };
    };

    // Build full period list from data range
    const startYear = validData[0].year;
    const startMonth = validData[0].month;
    const endYear = validData[validData.length - 1].year;
    const endMonth = validData[validData.length - 1].month;

    const periods = [];
    if (grouping === 'quarterly') {
      let y = startYear, q = Math.floor((startMonth - 1) / 3) + 1;
      const endQ = Math.floor((endMonth - 1) / 3) + 1;
      while (y < endYear || (y === endYear && q <= endQ)) {
        periods.push({ key: `${y}-Q${q}`, label: `${y} Q${q}`, ascents: [] });
        q += 1;
        if (q > 4) { q = 1; y += 1; }
      }
    } else {
      let y = startYear, m = startMonth;
      while (y < endYear || (y === endYear && m <= endMonth)) {
        periods.push({
          key: `${y}-${String(m).padStart(2, '0')}`,
          label: `${String(y).slice(2)} ${MONTH_SHORT[m - 1]}`,
          ascents: []
        });
        m += 1;
        if (m > 12) { m = 1; y += 1; }
      }
    }

    // Assign ascents to periods by key
    const periodByKey = Object.fromEntries(periods.map(p => [p.key, p]));
    validData.forEach(ascent => {
      const { key } = periodKeyFor(ascent);
      if (periodByKey[key]) periodByKey[key].ascents.push(ascent);
    });

    // Calculate CGP and CGE for each period
    return periods.map(period => {
      const cgp = calculateCGP(period.ascents, climbingStyle);
      const cge = calculateCGE(period.ascents, climbingStyle);

      // Count unique climbing days
      const uniqueDays = new Set(
        period.ascents.map(a => a['Ascent Date'].slice(0, 10))
      ).size;

      // Count clean ascents
      const cleanCount = period.ascents.filter(a => a.isClean).length;

      // Efficiency: how much of effort resulted in clean sends
      // efficiency = 2^(-diff) * 100 where diff = cge - cgp
      const diff = cge - cgp;
      const efficiency = cge > 0 ? Math.pow(2, -diff) * 100 : 0;

      return {
        period: period.label,
        cgp: cgp > 0 ? parseFloat(cgp.toFixed(2)) : null,
        cge: cge > 0 ? parseFloat(cge.toFixed(2)) : null,
        efficiency: efficiency > 0 ? parseFloat(efficiency.toFixed(1)) : null,
        ascents: period.ascents.length,
        cleanAscents: cleanCount,
        days: uniqueDays
      };
    }).filter(p => p.ascents > 0); // Only show periods with ascents
  }, [data, climbingStyle, grouping]);

  if (!chartData.length) {
    return <div className="text-center py-8">No data available</div>;
  }

  // Calculate domain
  const allValues = chartData.flatMap(d => [d.cgp, d.cge]).filter(v => v !== null);
  const minValue = Math.floor(Math.min(...allValues));
  const maxValue = Math.ceil(Math.max(...allValues));

  return (
    <div className="h-full flex flex-col">
      <div className="flex gap-4 mb-2 justify-center">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-600">Group by:</label>
          <select
            value={grouping}
            onChange={(e) => setGrouping(e.target.value)}
            className="text-sm border rounded px-2 py-1"
          >
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </div>
        <label className="flex items-center gap-1 text-sm text-gray-600">
          <input
            type="checkbox"
            checked={showEfficiency}
            onChange={(e) => setShowEfficiency(e.target.checked)}
            className="rounded"
          />
          Show Efficiency
        </label>
      </div>

      <div className="flex-1">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 5, right: showEfficiency ? 60 : 5, left: 0, bottom: 5 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 11 }}
              angle={grouping === 'monthly' ? -45 : 0}
              textAnchor={grouping === 'monthly' ? 'end' : 'middle'}
              height={grouping === 'monthly' ? 60 : 30}
            />
            <YAxis
              yAxisId="left"
              domain={[minValue - 1, maxValue + 1]}
              tick={{ fontSize: 12 }}
              label={{
                value: climbingStyle === CLIMBING_STYLES.BOULDER ? 'V-Grade Equivalent' : 'Ewbank Equivalent',
                angle: -90,
                position: 'insideLeft',
                style: { textAnchor: 'middle', fontSize: 12 }
              }}
            />
            {showEfficiency && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[0, 100]}
                tick={{ fontSize: 12 }}
                label={{
                  value: 'Efficiency %',
                  angle: 90,
                  position: 'insideRight',
                  style: { textAnchor: 'middle', fontSize: 12 }
                }}
              />
            )}
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                const data = payload[0]?.payload;
                return (
                  <div className="bg-white border border-gray-200 rounded shadow-lg p-3 text-sm">
                    <p className="font-semibold mb-2">{label}</p>
                    <p className="text-purple-600">CGP: {data.cgp?.toFixed(2) ?? 'N/A'}</p>
                    <p className="text-red-600">CGE: {data.cge?.toFixed(2) ?? 'N/A'}</p>
                    {showEfficiency && (
                      <p className="text-blue-600">Efficiency: {data.efficiency?.toFixed(1) ?? 'N/A'}%</p>
                    )}
                    <p className="text-gray-500 mt-1">
                      {data.cleanAscents} clean / {data.ascents} total ascents
                    </p>
                    <p className="text-gray-500">{data.days} climbing days</p>
                  </div>
                );
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />

            <Line
              yAxisId="left"
              type="monotone"
              dataKey="cgp"
              stroke="#9333ea"
              strokeWidth={2}
              name="CGP (Clean Performance)"
              dot={{ r: 3 }}
              connectNulls
            />
            <Line
              yAxisId="left"
              type="monotone"
              dataKey="cge"
              stroke="#dc2626"
              strokeWidth={2}
              name="CGE (Total Effort)"
              dot={{ r: 3 }}
              connectNulls
            />
            {showEfficiency && (
              <Bar
                yAxisId="right"
                dataKey="efficiency"
                fill="#3b82f6"
                opacity={0.3}
                name="Efficiency %"
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default CGPCGEChart;
